import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

/**
 * CCPA / GDPR cookie consent banner.
 *
 * Shows on first visit. Persists the user's choice to localStorage so the
 * banner doesn't reappear once they've decided. Both "Accept" and "Reject"
 * are recorded with a timestamp so we have an audit trail for compliance.
 *
 * We only set the strict-necessary cookies before consent — Google Analytics,
 * Intercom heartbeats, and other tracking should be gated on
 * `getCookieConsent() === "accepted"` elsewhere in the codebase.
 */

const STORAGE_KEY = "stacked_cookie_consent";

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed.choice || null;
  } catch {
    return null;
  }
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer the show by one tick so it doesn't flash during hydration.
    const t = setTimeout(() => {
      if (!getCookieConsent()) setVisible(true);
    }, 250);
    return () => clearTimeout(t);
  }, []);

  const record = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, timestamp: new Date().toISOString() })
      );
    } catch {
      /* storage disabled — fall through, banner will reappear next visit */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-[9999] bg-[#1a1f2e] border-t border-[#1ec8c8]/40 shadow-2xl"
    >
      <div className="max-w-6xl mx-auto px-5 py-4 flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="flex-1 text-sm text-gray-200">
          <p className="font-semibold text-white mb-1">We use cookies</p>
          <p className="text-gray-300 leading-relaxed">
            We use cookies and similar technologies to operate the site, remember
            your preferences, analyze traffic, and improve our service. You can
            accept all cookies, reject non-essential ones, or read our{" "}
            <Link to="/cookies-policy" className="text-[#1ec8c8] underline hover:text-[#34b8b3]">
              Cookies Policy
            </Link>{" "}
            for details.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-stretch md:self-auto">
          <button
            onClick={() => record("rejected")}
            className="px-4 py-2 rounded-lg border border-gray-600 text-gray-200 text-sm hover:bg-gray-700 transition-colors"
          >
            Reject non-essential
          </button>
          <button
            onClick={() => record("accepted")}
            className="px-4 py-2 rounded-lg bg-[#1ec8c8] text-black font-semibold text-sm hover:bg-[#34b8b3] transition-colors"
          >
            Accept all
          </button>
          <button
            onClick={() => record("dismissed")}
            aria-label="Close cookie banner"
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
